import './contextual.scss'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { InView } from 'react-intersection-observer'
import { navigate } from 'gatsby'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from '@reach/router'
import { axiosClient } from 'src/services/axiosInstance'
import { Image } from 'src/components/ui/Image'

import { getContextualFilter } from './utils'

interface ContextualFiltersProps {
  term: string
  productListName?: string
  sortOrder?: string
  numberOfProducts?: number
  /**
   * Filters config pre-fetched in getServerData. When an array is provided
   * (including empty), the client CMS fetch is skipped. `undefined` or `null`
   * triggers a client fallback fetch.
   */
  initialData?: ContextualFilter[] | null
}

interface ContextualFilterItem {
  nome: string
  url: string
  imgUrl: string
}

export interface ContextualFilter {
  categoria: string
  excludedTerms: string[]
  items: ContextualFilterItem[]
}

export function ContextualFilters({
  term,
  productListName,
  sortOrder,
  numberOfProducts,
  initialData,
}: ContextualFiltersProps) {
  const [allFilters, setAllFilters] = useState<ContextualFilter[]>(
    Array.isArray(initialData) ? initialData : []
  )

  const [isLoading, setIsLoading] = useState(!Array.isArray(initialData))
  const { search } = useLocation()
  const { pageData } = useGlobalContext()

  useEffect(() => {
    if (Array.isArray(initialData)) {
      setAllFilters(initialData)
      setIsLoading(false)

      return
    }

    setIsLoading(true)
    let isCancelled = false

    async function fetchCMSData() {
      try {
        const contentType = 'search-config'
        const builder = 'faststore'
        const params = new URLSearchParams(search)
        const pageId =
          params.get('pageId') ?? 'ef17df31-a983-4085-b5b1-9988558590f6'

        const versionId = params.get('versionId') ?? ''
        const url = `/api/cms/getPageData?contentType=${contentType}&builder=${builder}&pageId=${pageId}${
          versionId ? `&versionId=${versionId}` : ''
        }`

        const { data } = await axiosClient(url)

        const filters =
          data?.sections?.find(
            (section: { name?: string }) =>
              section.name === '[Search] Configuração de Filtros Contextuais'
          ) ?? null

        if (!isCancelled) {
          setAllFilters(filters?.data?.filters ?? [])
        }
      } catch (error) {
        console.error('Erro ao carregar filtros contextuais do CMS:', error)

        if (!isCancelled) {
          setAllFilters([])
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false)
        }
      }
    }

    fetchCMSData()

    return () => {
      isCancelled = true
    }
  }, [initialData, search])

  const contextualFilter = useMemo(() => {
    return getContextualFilter(term, allFilters)
  }, [term, allFilters])

  const standardObject = getStandardObject(pageData)

  const sendPromoFeatureViewedEvent = () => {
    dispatchAmplitudeEvent({
      eventName: 'Promo Feature Viewed',
      eventData: {
        type: 'Contextual',
        name: `${contextualFilter?.categoria}`,
      },
    })
  }

  const sendContextFilterAppliedEvent = (
    filterName: string,
    targetUrl: string
  ) => {
    if (typeof window === 'undefined') {
      return
    }

    const currentUrl = `${window.location.pathname}${window.location.search}`
    let nextPathAndSearch = ''

    try {
      const nextUrl = new URL(targetUrl, window.location.origin)

      nextPathAndSearch = `${nextUrl.pathname}${nextUrl.search}`
    } catch {
      return
    }

    if (currentUrl === nextPathAndSearch) {
      return
    }

    dispatchAmplitudeEvent({
      eventName: 'Context Filter Applied',
      eventData: {
        ...standardObject,
        'context name': filterName,
        'product list name':
          productListName ?? `Página de lista de produtos: ${term}`,
        'sort order': sortOrder ?? null,
        'number of products': numberOfProducts ?? 0,
      },
    })
  }

  if (isLoading || !contextualFilter) {
    return null
  }

  return (
    <InView
      className="scroolbar-contextual-filters w-full min-w-0 max-w-full flex flex-nowrap gap-4 mb-4 pb-2.5 md:pb-10 md:border-b-sm md:border-[#e1e4e7] md:mb-11"
      as="section"
      triggerOnce
      onChange={(inView) => {
        if (inView) {
          sendPromoFeatureViewedEvent()
        }
      }}
    >
      {contextualFilter?.items.map((item) => (
        <button
          className="contextualcard shrink-0 flex flex-col items-center w-[105px] min-w-[105px] md:w-[164px] md:min-w-[164px] gap-2 md:gap-[9px]"
          key={item.nome}
          type="button"
          onClick={() => {
            sendContextFilterAppliedEvent(item.nome, item.url)
            navigate(item.url)
          }}
        >
          <span className="flex items-center justify-center w-full h-[88px] md:h-[101px] rounded-[6px] bg-[#F5F5F5] overflow-hidden">
            <Image
              alt={`${item.nome}`}
              src={item.imgUrl}
              width={100}
              height={100}
              sizes="(min-width: 768px) 81px, 56px"
              className="h-[56px] w-[56px] md:h-[81px] md:w-[81px] object-contain mix-blend-multiply"
            />
          </span>
          <span className="mobile-body-semibold-text3 desktop-body-semibold-text3 text-restructure-primary text-center w-full whitespace-normal">
            {item.nome}
          </span>
        </button>
      ))}
    </InView>
  )
}
